import React, { useEffect, useState } from "react";
import {
    Box, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Button,
    Chip, CircularProgress, Stack, IconButton, Tooltip
} from "@mui/material";
import { Cancel, CheckCircle, DeleteForever } from "@mui/icons-material";
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc, orderBy, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import Swal from "sweetalert2";

function Approvals() {
    const [pendingList, setPendingList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);

    useEffect(() => { 
        fetchPendingApprovals(); 
    }, []);

    const fetchPendingApprovals = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, "wallet_approvals"),
                where("status", "==", "Pending"),
                orderBy("createdAt", "desc")
            );
            const snap = await getDocs(q);
            setPendingList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error("Fetch Approvals Error:", e);
        } finally {
            setLoading(false);
        }
    };

    // Commit changes: Purges targeted row from ledger records index
    const handleApproveDelete = async (approval) => {
        const result = await Swal.fire({
            title: 'Approve Deletion Request?',
            text: `Authorize complete erasure of transaction ${approval.receiptNo || 'N/A'} for ${approval.studentName}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, Confirm Erasure'
        });

        if (!result.isConfirmed) return;

        setProcessing(true);
        try {
            // Step 1: Perform the fundamental structural purge action inside the core ledger collection
            await deleteDoc(doc(db, "wallet_transactions", approval.transactionId));

            // Step 2: Mark current validation ticket status inside approvals workflow configuration
            await updateDoc(doc(db, "wallet_approvals", approval.id), {
                status: "Approved",
                resolvedAt: Timestamp.now()
            });

            Swal.fire("Deleted!", "Ledger item completely eradicated.", "success");
            fetchPendingApprovals();
        } catch (e) {
            Swal.fire("Execution Error", e.message, "error");
        } finally {
            setProcessing(false);
        }
    };

    const handleRejectDelete = async (approval) => {
        const result = await Swal.fire({
            title: 'Reject Deletion Request?',
            text: 'Are you sure you want to deny this wallet modification ticket request?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#475569',
            cancelButtonColor: '#64748b'
        });

        if (!result.isConfirmed) return;

        setProcessing(true);
        try {
            await updateDoc(doc(db, "wallet_approvals", approval.id), {
                status: "Rejected",
                resolvedAt: Timestamp.now()
            });

            Swal.fire("Rejected", "Ticket has been dismissed.", "info");
            fetchPendingApprovals();
        } catch (e) {
            Swal.fire("Execution Error", e.message, "error");
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Box p={3} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
            <Box mb={4}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    Administrative <span style={{ color: "#0d9488" }}>Approvals Center</span>
                </Typography>
                <Typography variant="caption" color="textSecondary">Audit and manage sensitive transaction ledger update request records</Typography>
            </Box>

            {loading ? (
                <Stack alignItems="center" py={10}><CircularProgress sx={{ color: "#0d9488" }} /></Stack>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: "#f8fafc" }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Student Info</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Receipt No</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: "#64748b" }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Reason for Deletion</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Requested By</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700, color: "#64748b" }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pendingList.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell sx={{ fontWeight: 600, color: "#0f172a" }}>{row.studentName}</TableCell>
                                    <TableCell>
                                        <Chip label={row.receiptNo || 'Manual'} size="small" sx={{ fontWeight: 700, bgcolor: "#e2e8f0" }} />
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: "#e11d48" }}>
                                        ₹{row.amount?.toLocaleString('en-IN')}
                                    </TableCell>
                                    <TableCell sx={{ color: "#475569", fontStyle: "italic" }}>"{row.reason}"</TableCell>
                                    <TableCell sx={{ fontSize: "0.85rem" }}>{row.requestedBy}</TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <Button 
                                                variant="contained" size="small" color="error" startIcon={<DeleteForever />}
                                                disabled={processing} onClick={() => handleApproveDelete(row)}
                                                sx={{ textTransform: "none", borderRadius: "8px" }}
                                            >
                                                Approve
                                            </Button>
                                            <IconButton 
                                                color="textSecondary" disabled={processing} onClick={() => handleRejectDelete(row)}
                                                sx={{ bgcolor: "#e2e8f0", '&:hover': { bgcolor: "#cbd5e1" } }}
                                            >
                                                <Cancel fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {pendingList.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 8, color: "#64748b" }}>
                                        No pending transaction wallet deletion exceptions found inside queue.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export default Approvals;